import { Badge, Box, Button, DataList, Grid, GridItem, HStack, Separator, Stack, Table, Text } from "@chakra-ui/react"
import { Form, UI } from "components"
import { Mask, Styles } from "config"
import useOrders from "hooks/useOrders"
import useReportSalesGeneral from "hooks/useReportSalesGeneral"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import Moment from "moment"

export default function Orders() {
    // Datas iniciais (objetos Date)
    const initialPeriod = [Moment().subtract(3, "months").toDate(), Moment().toDate()]

    const [page, setPage] = useState(1)
    const [term, setTerm] = useState(null)
    const [status, setStatus] = useState(null)
    const [type, setType] = useState(null)
    const [period, setPeriod] = useState(initialPeriod)

    // Converte period para o formato YYYY-MM-DD para enviar ao backend
    const validPeriod =
        period && period[0] instanceof Date && period[1] instanceof Date
            ? [Moment(period[0]).format('YYYY-MM-DD'), Moment(period[1]).format('YYYY-MM-DD')]
            : null

    const list = useOrders(page, term, type, status, validPeriod)
    const sales = useReportSalesGeneral()

    useEffect(() => {
        setPage(1)
    }, [term, status, type, period])

    const columns = [
        { children: '' },
        { children: 'Consultor' },
        { children: 'Detalhes' },
        { children: 'Valor', textAlign: 'right' },
        { children: 'Data', textAlign: 'right' }
    ]

    const handleResetFilters = () => {
        setStatus(null)
        setType(null)
        setPeriod(initialPeriod) // volta para o intervalo inicial
    }

    return (
        <>
            <UI.Header title="Vendas" />

            <Grid templateColumns={{ base: '1fr', sm: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                <GridItem>
                    <Stack>
                        <Text fontSize="sm" color="GrayText" mb={4}>Resumo de vendas:</Text>
                        <DataList.Root orientation="horizontal" gap={2} divideY="1px">
                            <DataList.Item>
                                <DataList.ItemLabel>🟡 Em análise:</DataList.ItemLabel>
                                <DataList.ItemValue>{sales.data?.analyzing}</DataList.ItemValue>
                            </DataList.Item>
                            <DataList.Item>
                                <DataList.ItemLabel>🟢 Aprovados:</DataList.ItemLabel>
                                <DataList.ItemValue>{sales.data?.approved}</DataList.ItemValue>
                            </DataList.Item>
                            <DataList.Item>
                                <DataList.ItemLabel>🔵 Em trânsito:</DataList.ItemLabel>
                                <DataList.ItemValue>{sales.data?.transit}</DataList.ItemValue>
                            </DataList.Item>
                            <DataList.Item>
                                <DataList.ItemLabel>🔵 Retirada:</DataList.ItemLabel>
                                <DataList.ItemValue>{sales.data?.withdraw}</DataList.ItemValue>
                            </DataList.Item>
                        </DataList.Root>
                    </Stack>
                </GridItem>
            </Grid>

            <Separator my={4} />

            <UI.Searchable placeholder="Procurar pelo código do pedido, email ou nome..." onChange={setTerm} value={term} />

            <Box mt={4} />

            <UI.Table
                loading={list.isFetching}
                loadingText="Buscando vendas..."
                label="Histórico de pedidos:"
                columns={columns}
                data={list.data?.data}
                meta={list.data?.meta}
                onPageChange={value => setPage(value.page)}
                Row={Row}
            >
                <Form.Select
                    selected={status}
                    placeholder="Todos os status"
                    items={[
                        { label: 'Todos os status', value: null },
                        { label: '🟡 Em análise', value: 12 },
                        { label: '🟢 Aprovado', value: 3 },
                        { label: '🔴 Cancelado', value: 5 },
                        { label: '🔵 Em trânsito', value: 9 },
                        { label: '🔵 Aguardando retirada', value: 11 },
                        { label: '⚪ Concluído', value: 5 }
                    ]}
                    onChange={e => setStatus(e.target.value)}
                />

                <Form.Select
                    selected={type}
                    placeholder="Todos os tipos"
                    items={[
                        { label: 'Todos os tipos', value: null },
                        { label: 'Produtos', value: 1 },
                        { label: 'Adesão', value: 3 }
                    ]}
                    onChange={e => setType(e.target.value)}
                />

                {/* Filtro de período */}
                <Form.Interval
                    initial={initialPeriod}
                    onSelect={(p) => setPeriod(p)}
                />

                <UI.Show visible={!!status || !!type || !!validPeriod}>
                    <Button variant="ghost" size="xs" onClick={handleResetFilters}>
                        <UI.Icon name="cancel" /> Limpar filtros
                    </Button>
                </UI.Show>
            </UI.Table>
        </>
    )
}

const Row = ({ data }) => {
    const navigate = useNavigate()
    const status = Styles.orderStatus(data?.status)

    return (
        <Table.Row cursor="pointer" _hover={{ bg: 'rgba(0,0,0,0.1)' }} onClick={() => navigate(`/pedidos/${data?.id}`)}>
            <Table.Cell fontSize="xs">{data?.cod}</Table.Cell>
            <Table.Cell>
                <UI.Tag.Avatar size="lg" name={data?.consultant?.customer?.name} src={data?.consultant?.customer?.avatar_url} />
            </Table.Cell>
            <Table.Cell>
                <HStack spacing={2}>
                    <Badge colorPalette={status.colorPalette}>{status.label}</Badge>
                    {data?.type === 3 ? <Badge colorPalette="purple">Adesão</Badge> : <Badge>Produtos</Badge>}
                    <UI.Show visible={data?.shipping_type === 1}><Badge>Para envio</Badge></UI.Show>
                    <UI.Show visible={data?.shipping_type === 2}><Badge>Para retirada</Badge></UI.Show>
                </HStack>
            </Table.Cell>
            <Table.Cell textAlign="right" fontWeight="bold" color={`${status.colorPalette}.fg`}>
                <UI.Number.Amount value={data?.total_amount} />
            </Table.Cell>
            <Table.Cell textAlign="right" fontSize="xs">{Mask.dateTime(data?.created_at)}</Table.Cell>
        </Table.Row>
    )
}
